import React, { useRef, useEffect, useState } from 'react'
import styled from 'styled-components'

const OtpContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${props => props.gap || '12px'};
  width: 100%;
  max-width: ${props => props.maxWidth || '400px'};
  margin: ${props => props.margin || '20px auto'};
  
  @media (max-width: 768px) {
    gap: ${props => props.mobileGap || '8px'};
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    gap: ${props => props.smallMobileGap || '6px'};
  }
`

const OtpInputBox = styled.input`
  width: ${props => props.boxSize || '56px'};
  height: ${props => props.boxSize || '56px'};
  border: 2px solid ${props => props.borderColor || '#D9D9D9'};
  border-radius: ${props => props.borderRadius || '12px'};
  background: ${props => props.background || '#FFFFFF'};
  font-size: ${props => props.fontSize || '24px'};
  font-weight: ${props => props.fontWeight || '600'};
  color: ${props => props.textColor || '#242424'};
  text-align: center;
  transition: all 0.2s ease-in-out;
  font-family: ${props => props.fontFamily || 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'};
  
  &:focus {
    outline: none;
    border-color: ${props => props.focusBorderColor || '#624de3'};
    box-shadow: 0 0 0 3px ${props => props.focusShadowColor || 'rgba(98, 77, 227, 0.15)'};
    transform: scale(1.05);
  }
  
  &:hover:not(:disabled) {
    border-color: ${props => props.hoverBorderColor || '#999'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: ${props => props.mobileBoxSize || '48px'};
    height: ${props => props.mobileBoxSize || '48px'};
    font-size: ${props => props.mobileFontSize || '20px'};
  }
  
  @media (max-width: 480px) {
    width: ${props => props.smallMobileBoxSize || '44px'};
    height: ${props => props.smallMobileBoxSize || '44px'};
    font-size: ${props => props.smallMobileFontSize || '18px'};
    border-radius: ${props => props.smallMobileBorderRadius || '10px'};
  }
`

const OtpInput = ({
  value = '',
  onChange,
  numInputs = 6,
  onComplete,
  disabled = false,
  autoFocus = true,
  // Styling props
  boxSize = '56px',
  mobileBoxSize = '48px',
  smallMobileBoxSize = '44px',
  fontSize = '24px',
  mobileFontSize = '20px',
  smallMobileFontSize = '18px',
  fontWeight = '600',
  gap = '12px',
  mobileGap = '8px',
  smallMobileGap = '6px',
  maxWidth = '400px',
  margin = '20px auto',
  borderColor = '#D9D9D9',
  focusBorderColor = '#624de3',
  hoverBorderColor = '#999',
  focusShadowColor = 'rgba(98, 77, 227, 0.15)',
  background = '#FFFFFF',
  textColor = '#242424',
  borderRadius = '12px',
  smallMobileBorderRadius = '10px',
  fontFamily = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  // Dark mode support
  darkMode = false,
}) => {
  const inputRefs = useRef([])
  // Ensure value is always a string to prevent uncontrolled/controlled warning
  const normalizedValue = value != null ? String(value) : ''
  const [otp, setOtp] = useState(normalizedValue)

  // Update internal state when value prop changes
  useEffect(() => {
    const newValue = value != null ? String(value) : ''
    if (newValue !== otp) {
      setOtp(newValue)
    }
  }, [value, otp])

  // Auto-focus first input on mount
  useEffect(() => {
    if (autoFocus && inputRefs.current[0] && !disabled) {
      inputRefs.current[0].focus()
    }
  }, [autoFocus, disabled])

  const handleChange = (index, newValue) => {
    // Only allow single digit
    if (newValue.length > 1) return

    // Only allow numbers
    if (newValue && !/^\d$/.test(newValue)) return

    const newOtp = otp.split('')
    newOtp[index] = newValue
    const updatedOtp = newOtp.join('')

    setOtp(updatedOtp)
    onChange?.(updatedOtp)

    // Auto-focus next input if value entered
    if (newValue && index < numInputs - 1) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all inputs are filled
    if (updatedOtp.length === numInputs && updatedOtp.split('').every(digit => digit !== '')) {
      onComplete?.(updatedOtp)
    }
  }

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        // If current input is empty, focus previous and clear it
        inputRefs.current[index - 1]?.focus()
        const newOtp = otp.split('')
        newOtp[index - 1] = ''
        const updatedOtp = newOtp.join('')
        setOtp(updatedOtp)
        onChange?.(updatedOtp)
      } else if (otp[index]) {
        // If current input has value, clear it
        const newOtp = otp.split('')
        newOtp[index] = ''
        const updatedOtp = newOtp.join('')
        setOtp(updatedOtp)
        onChange?.(updatedOtp)
      }
    }
    // Handle arrow keys
    else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < numInputs - 1) {
      inputRefs.current[index + 1]?.focus()
    }
    // Handle delete
    else if (e.key === 'Delete') {
      const newOtp = otp.split('')
      newOtp[index] = ''
      const updatedOtp = newOtp.join('')
      setOtp(updatedOtp)
      onChange?.(updatedOtp)
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text/plain').trim()
    
    // Only process if pasted data contains only digits
    if (!/^\d+$/.test(pastedData)) return

    // Take first numInputs digits
    const digits = pastedData.slice(0, numInputs).split('')
    const newOtp = digits.join('').padEnd(numInputs, '')
    
    setOtp(newOtp)
    onChange?.(newOtp)

    // Focus the last filled input or the last input
    const lastFilledIndex = Math.min(digits.length - 1, numInputs - 1)
    inputRefs.current[lastFilledIndex]?.focus()

    // Auto-submit if all inputs are filled
    if (newOtp.length === numInputs && newOtp.split('').every(digit => digit !== '')) {
      onComplete?.(newOtp)
    }
  }

  const handleFocus = (index, e) => {
    // Select all text when focused
    e.target.select()
  }

  // Apply dark mode styles if enabled
  const finalBackground = darkMode ? '#383838' : background
  const finalTextColor = darkMode ? '#FFFFFF' : textColor
  const finalBorderColor = darkMode ? '#707070' : borderColor
  const finalFocusBorderColor = darkMode ? '#624de3' : focusBorderColor

  return (
    <OtpContainer
      gap={gap}
      mobileGap={mobileGap}
      smallMobileGap={smallMobileGap}
      maxWidth={maxWidth}
      margin={margin}
    >
      {Array.from({ length: numInputs }).map((_, index) => (
        <OtpInputBox
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={otp[index] ?? ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={handleFocus}
          disabled={disabled}
          boxSize={boxSize}
          mobileBoxSize={mobileBoxSize}
          smallMobileBoxSize={smallMobileBoxSize}
          fontSize={fontSize}
          mobileFontSize={mobileFontSize}
          smallMobileFontSize={smallMobileFontSize}
          fontWeight={fontWeight}
          borderColor={finalBorderColor}
          focusBorderColor={finalFocusBorderColor}
          hoverBorderColor={hoverBorderColor}
          focusShadowColor={focusShadowColor}
          background={finalBackground}
          textColor={finalTextColor}
          borderRadius={borderRadius}
          smallMobileBorderRadius={smallMobileBorderRadius}
          fontFamily={fontFamily}
        />
      ))}
    </OtpContainer>
  )
}

export default OtpInput

